import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IInvitation } from '@app/models/signup';
import { SignupCheckService } from '@app/shared-signup/signup-check.service';
import {
  ConfigService,
  EscapeHtmlPipe,
  NotificationService,
  ScreenLoaderService,
  ValidationCheck,
} from '@infomaniak/angular-common';
import { translate } from '@ngneat/transloco';
import { SwiperConfig } from 'ngx-swiper-wrapper';
import { Subject } from 'rxjs';

import { tState } from './steps/shared/steps.model';
import { ActivatedRouteHasNewDesignParam } from '@app/core/helpers/activated-route-has-param/new-design.helper';
import { HOST, LogUser } from '@app/signup/shared/login-logout-user';
import { getPasswordValidationMessage } from '@app/shared/password-validation';
import { LoaderService } from '@app/core/loader.service';
import { loaderGifPath } from '@app/shared/helpers/get-theme-icon';
import { take } from 'rxjs/operators';
import { UpdatePersonnalInformation } from '@app/store/actions/signup.action';
import { Store } from '@ngxs/store';

@Component({
  selector: 'signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
})
export class SignupComponent implements OnInit, OnDestroy {
  invitation: IInvitation = window['INVITATION'];
  user = window['CONST_USER'];
  state: tState = window['CURRENT_STATE'];
  endpoints = window['ENDPOINTS'];
  loading = true;
  isSuccess = false;
  imgLoaded = false;
  imgLoadedCount = 0;
  isMobile: boolean;
  inputType = 'password';
  escapeHTML = new EscapeHtmlPipe();
  webmailPassword = false;
  webmailMigration = false;
  isPublicCloudInvitation = false;
  isB2BPartnerInvitation = false;
  isB2BCustomerInvitation = false;
  isB2BPartnerInvitationError = false;
  isB2BCustomerInvitationError = false;
  managerUrlToRedirect = null;
  form: FormGroup;
  passwordValidation = new ValidationCheck();

  swiperconf = new SwiperConfig({
    direction: 'horizontal',
    slidesPerView: 1,
    loop: true,
    keyboard: true,
    mousewheel: false,
    scrollbar: false,
    navigation: {
      nextEl: '.next-step1',
      prevEl: '.prev-step1',
    },
    pagination: false,
    watchOverflow: true,
    threshold: 20,
    autoplay: {
      delay: 4000,
      disableOnInteraction: false,
    },
  });

  newDesign = false;

  private onDestroy$ = new Subject<boolean>();
  readonly loaderGifPath = loaderGifPath;

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private screenLoaderService: ScreenLoaderService,
    private loaderService: LoaderService,
    private notificationService: NotificationService,
    public checkService: SignupCheckService,
    private breakpointObserver: BreakpointObserver,
    private configService: ConfigService,
    private store: Store
  ) {
    this.route.queryParamMap.pipe(take(1)).subscribe((queryParams) => {
      if (queryParams.get('firstname') || queryParams.get('lastname') || queryParams.get('phone')) {
        this.store.dispatch(
          new UpdatePersonnalInformation({
            firstname: queryParams.get('firstname') ?? '',
            lastname: queryParams.get('lastname') ?? '',
            reminder_phone: '+' + queryParams.get('phone') ?? '',
          })
        );
      }
    });
  }

  get displayFormError(): boolean {
    const passwordCtrl = this.form.get('password');

    return (
      passwordCtrl.invalid &&
      passwordCtrl.touched &&
      passwordCtrl.dirty &&
      !passwordCtrl.hasError('required') &&
      !passwordCtrl.hasError('maxlength')
    );
  }

  get isPasswordRestricted(): boolean {
    return !this.checkService.isEmailForced() &&
      this.invitation.extras?.ksuite_mailbox &&
      this.invitation.status !== 'user_already_in_ksuite' &&
      this.invitation.type === 'ksuite'
      ? this.form.get('password').invalid
      : false;
  }

  get brandingLogoPath(): string {
    let infomaniakLogoPath = '/assets/images/logo-infomaniak.svg';
    switch (window['CURRENT_ORDER_PARAMETERS']?.custom_branding) {
      case 'ksuite-net-plus-leman':
        return '/assets/images/special-offer-branding/infomaniak-x-net-plus-logo.svg';
      default:
        return infomaniakLogoPath;
    }
  }

  get landingHeaderBaseWording(): string {
    let standardWording = translate(
      'Avec votre compte, vous accederez a tous les outils <strong>indispensables</strong> pour travailler en equipe !'
    );
    switch (window['CURRENT_ORDER_PARAMETERS']?.custom_branding) {
      case 'ksuite-net-plus-leman':
        return translate('Pour profitez de l’offre kSuite avec net+ Léman, vous devez avoir un compte Infomaniak.');
      default:
        return standardWording;
    }
  }

  ngOnInit() {
    // Redirect to steps of create account if param is set
    if (this.route.snapshot.queryParamMap.get('goToCreate') === '1') {
      this.createAccount();
    }

    if (this.checkService.currentAccountIsMyKsuite() && this.checkService.isDuringOrder()) {
      this.createAccount();
    }

    this.isB2BPartnerInvitationError =
      this.isInErrorState() && 'invitation_b2b_partner' === this.state && this.invitation?.status !== 'expired';
    this.isB2BCustomerInvitationError =
      this.isInErrorState() &&
      'invitation_b2b_customer' === this.state &&
      this.invitation?.status !== 'expired' &&
      this.invitation?.status !== 'deleted';

    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium]).subscribe((result) => {
      if (result.breakpoints[Breakpoints.XSmall]) {
        this.isMobile = true;
      }
      this.isMobile = false;
    });
    if (!this.isInErrorState()) {
      if (this.state === 'invitation_webmail' && this.invitation && this.invitation.status === 'pending_password') {
        this.webmailPassword = true;
      } else if ('invitation_public_cloud' === this.state) {
        this.isPublicCloudInvitation = true;
      } else if ('invitation_b2b_partner' === this.state) {
        this.isB2BPartnerInvitation = true;
      } else if ('invitation_b2b_customer' === this.state && this.invitation.status !== 'pending_create') {
        this.isB2BCustomerInvitation = true;
      } else if (this.state === 'invitation_password_reset') {
        this.webmailPassword = true;
      } else if (
        (this.checkService.isWorkspace() &&
          (!this.checkService.isDuringInvitation() || this.checkService.isInTransfer())) ||
        this.checkService.isMyKsuite()
      ) {
        this.createAccount();
      } else if (this.checkService.isConnected() && this.checkService.isInTransfer()) {
        this.router.navigate(['/change_account']);
      } else if (
        this.checkService.isConnected() &&
        !this.checkService.isDuringInvitation() &&
        !this.checkService.isDuringOrder()
      ) {
        const url = new URL(window.location.href);
        if (url?.searchParams?.get('new')) {
          this.router.navigate(['/signup/steps'], { state: ['change_account'] });
        } else {
          this.router.navigate(['/change_account']);
        }
      }
      this.screenLoaderService.start();
    }

    this.form = new FormGroup({
      password: new FormControl(null, [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(72),
        this.passwordValidator.bind(this),
      ]),
    });

    this.loading = false;
  }

  onImgLoaded() {
    this.imgLoadedCount++;
    if (this.imgLoadedCount === this.imagesDiplayed().length) {
      this.imgLoaded = true;
      this.screenLoaderService.stop();
    }
  }

  createAccount() {
    this.screenLoaderService.start();
    // cut the current route as '/routeA/routeB' => ['routeA','routeB']
    const currentRoute = ((this.route as any)._routerState.snapshot.url as string).split('/').slice(1);
    this.router.navigate(['steps'], {
      relativeTo: this.route.parent,
      state: currentRoute,
      queryParams: this.route.snapshot.queryParams,
    });
  }

  createAccountWebmailMigration() {
    this.loading = true;
    this.screenLoaderService.start();
    this.router.navigate(['/signup/webmail1/steps']);
  }

  login() {
    let data = {};
    let skipRedirect = false;
    if (this.checkService.isDuringInvitation() && this.checkService.isConnected()) {
      let uri = '';

      if (this.state === 'invitation_ksuite') {
        if (
          this.getInvitationKsuiteEmailOfKsuite() &&
          !this.checkService.isEmailForced() &&
          this.invitation.status !== 'user_already_in_ksuite'
        ) {
          skipRedirect = true;
          data = { linked_mailbox_password: this.form.get('password').value };
        }
      }
      switch (this.state) {
        case 'invitation_account':
        case 'invitation_drive':
        case 'invitation_webmail':
        case 'invitation_kchat':
        case 'invitation_ksuite':
          // ex: invitation_ksuite => /invitation/ksuite/accept
          uri = `/invitation/${this.state.split('_')[1]}/accept`;
          break;
        case 'invitation_webmail_ksuite':
          data = { linked_mailbox_password: this.form.get('password').value };
          uri = '/invitation/webmail/accept';
          break;
        case 'invitation_transfer':
          return this.router.navigate(['/change_account']);
        default:
          throw new Error(`Signup.login CURRENT_STATE ${this.state} not supported`);
      }

      this.acceptInvitation(uri, data, skipRedirect);
    } else {
      window.location.assign(window['ENDPOINTS']?.login || 'login.infomaniak.com');
    }
  }

  changeAccount() {
    this.loaderService.set(true);
    if (window['CONST_USER']?.id || !window['LOGIN_ENDPOINT']) {
      return LogUser.Action.logoutWithLoginRedirect();
    }
    if (window['LOGIN_ENDPOINT']) {
      this.http
        .get(
          `${window['LOGIN_ENDPOINT']}/api/check?uri=${window['PROJECT_SETUP']?.link ?? '/'}&project=${
            window['PROJECT_SETUP']?.status ?? HOST
          }`
        )
        .subscribe(
          (response: any) => {
            if (response?.data?.status === 'no') {
              return LogUser.Action.login({
                project: window['PROJECT_SETUP']?.status,
                uri: window['PROJECT_SETUP']?.link,
              });
            } else {
              return LogUser.Action.logoutWithLoginRedirect();
            }
          },
          (error: any) => {
            return LogUser.Action.login({
              project: window['PROJECT_SETUP']?.status,
              uri: window['PROJECT_SETUP']?.link,
            });
          }
        );
    } else {
      this.loaderService.set(false);
    }
  }

  logout() {
    LogUser.Action.logout();
  }

  onLandingButtonAction(evt) {
    switch (evt) {
      case 'createAccount':
        if (ActivatedRouteHasNewDesignParam(this.route)) {
          this.newDesign = true;
        } else if (this.webmailMigration) {
          this.createAccountWebmailMigration();
        } else {
          this.createAccount();
        }
        break;
      case 'changeAccount':
        this.changeAccount();
        break;
      case 'login':
        this.login();
        break;
      case 'logout':
        LogUser.Action.logout();
        break;
      default:
        throw new Error(`onLandingButtonAction ${evt} not supported`);
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  acceptInvitation(uri: string, data: any, skipRedirect: boolean) {
    this.loading = true;
    this.http.post(uri, data).subscribe(
      (response: any) => {
        this.managerUrlToRedirect = response?.data?.redirect;
        if (skipRedirect) {
          this.isSuccess = true;
          this.loading = false;
        } else {
          window.location.assign(response.data && response.data.redirect);
        }
      },
      (error: any) => {
        const code = this.configService.findCode(error);
        this.notificationService.error(
          code
            ? translate(code)
            : translate(
                'Une erreur est survenue. Merci de reessayer ulterieurement ou de nous contacter si le probleme persiste.'
              ),
          null,
          null,
          { icon: 'icon-warning danger' }
        );
        this.loading = false;
      }
    );
  }

  invitationDetails(): { title: string; p: string; type: string } {
    const accountName =
      this.invitation?.account?.organization_name &&
      this.escapeHTML.transform(this.invitation.account.organization_name);
    switch (this.state) {
      case 'invitation_webmail':
        const email =
          this.invitation?.extras?.webmail?.email && this.escapeHTML.transform(this.invitation.extras.webmail.email);

        return this.checkService.isConnected()
          ? {
              title: translate('$accountName$ vous a cree une adresse mail', { accountName }),
              p: translate("Acceptez l'invitation pour utiliser votre nouvelle adresse mail <strong>$email$</strong>", {
                email,
              }),
              type: this.state,
            }
          : {
              title: translate('$accountName$ vous a cree une adresse mail', { accountName }),
              p:
                this.invitation && this.invitation.status === 'pending_auth'
                  ? translate(
                      "Connectez-vous et acceptez l'invitation pour utiliser votre nouvelle adresse mail <strong>$email$</strong>",
                      { email }
                    )
                  : translate('Creez votre compte et utilisez votre nouvelle adresse mail <strong>$email$</strong>', {
                      email,
                    }),
              type: this.state,
            };
      case 'invitation_drive':
        const driveName =
          this.invitation &&
          this.invitation.extras &&
          this.invitation.extras.drive &&
          this.invitation.extras.drive.name &&
          this.escapeHTML.transform(this.invitation.extras.drive.name);
        const accountMail = this.invitation?.email && this.invitation?.email?.toLowerCase();

        if (this.invitation && accountMail) {
          if (this.invitation.status === 'pending_auth') {
            return {
              title: translate('$accountName$ vous invite a utiliser un drive', { accountName }),
              p: translate(
                "Connectez-vous au compte <strong>$accountMail$</strong> pour accepter l'invitation et acceder au drive : <strong>$driveName$</strong>",
                { driveName, accountMail }
              ),
              type: this.state,
            };
          }
          if (this.invitation.status === 'pending_create') {
            return {
              title: translate('$accountName$ vous invite a utiliser un drive', { accountName }),
              p: translate(
                "Creez votre compte avec l'adresse <strong>$accountMail$</strong> pour accepter l'invitation et acceder au drive : <strong>$driveName$</strong>",
                { driveName, accountMail }
              ),
              type: this.state,
            };
          }
        }
        // default
        return {
          title: translate('$accountName$ vous invite a utiliser un drive', { accountName }),
          p: translate("Acceptez l'invitation pour acceder au drive : <strong>$driveName$</strong>", { driveName }),
          type: this.state,
        };

      case 'invitation_kchat':
        const emailInvitation = this.invitation?.email && this.invitation?.email?.toLowerCase();

        if (this.invitation && emailInvitation) {
          if (this.invitation.status === 'pending_auth') {
            return {
              title: translate('$accountName$ vous invite a utiliser kChat', { accountName }),
              p: translate(
                "Connectez-vous au compte <strong>$emailInvitation$</strong> pour accepter l'invitation et acceder a kChat",
                { emailInvitation }
              ),
              type: this.state,
            };
          }
          if (this.invitation.status === 'pending_create') {
            return {
              title: translate('$accountName$ vous invite a utiliser kChat', { accountName }),
              p: translate(
                "Creez votre compte avec l'adresse <strong>$emailInvitation$</strong> pour accepter l'invitation et acceder a kChat",
                { emailInvitation }
              ),
              type: this.state,
            };
          }
        }
        // default
        return {
          title: translate('$accountName$ vous invite a utiliser kChat', { accountName }),
          p: translate("Acceptez l'invitation pour acceder a kChat"),
          type: this.state,
        };
      case 'invitation_account':
        return this.checkService.isConnected()
          ? {
              title: translate('<strong>$accountName$</strong> vous invite', { accountName }),
              p: translate('Vous possedez deja un compte Infomaniak, rejoignez <strong>$name$</strong>', {
                name: accountName,
              }),
              type: this.state,
            }
          : {
              title: translate('<strong>$accountName$</strong> vous invite a creer votre compte', { accountName }),
              p: translate(
                'Avec votre compte, vous accederez a tous les outils <strong>indispensables</strong> pour travailler en equipe !'
              ),
              type: this.state,
            };
      case 'invitation_b2b_customer':
        const invitedByB2bMail = this.invitation?.email?.toLowerCase();

        return {
          title: translate('<strong>$accountName$</strong> souhaite collaborer avec vous', { accountName }),
          p: translate(
            "Creez votre compte avec l'adresse <strong>$invitedByB2bMail$</strong> pour accepter l'invitation.",
            { invitedByB2bMail }
          ),
          type: this.state,
        };
      default:
        return null;
    }
  }

  isInErrorState() {
    if (!this.state) {
      return false;
    }
    return (
      this.invitation &&
      this.invitation.status.indexOf('pending') < 0 &&
      this.state !== 'signup' &&
      this.invitation.status !== 'user_already_in_ksuite'
    );
  }

  getSwiperConfig() {
    return {
      direction: 'horizontal',
      slidesPerView: 1,
      init: true,
      loop: true,
      breakpoints: {
        767: {
          slidesPerView: 1,
        },
      },
      keyboard: true,
      mousewheel: false,
      scrollbar: false,
      navigation: {
        nextEl: '.next-step1',
        prevEl: '.prev-step1',
      },
      pagination: false,
      watchOverflow: true,
      threshold: 20,
      autoplay: {
        delay: 4000,
        disableOnInteraction: false,
      },
      noSwiping: false,
    } as SwiperConfig;
  }

  imagesDiplayed() {
    switch (this.state) {
      case 'invitation_webmail':
        return ['mail'];
      case 'invitation_drive':
        return ['drive'];
      case 'invitation_kchat':
        return ['kchat'];
      default:
        return ['drive', 'mail', 'calendar', 'contacts'];
    }
  }

  getInvitationKsuiteEmailOfKsuite(): string {
    return (
      this.invitation?.extras?.ksuite_mailbox ||
      (this.isKsuiteUserMailbox() ? this.invitation?.extras?.webmail?.email : undefined)
    );
  }

  isKsuiteUserMailbox(): boolean {
    return this.invitation?.extras?.ksuite_user_maibox;
  }

  isPasswordAsk(): boolean {
    return this.isKsuiteUserMailbox() || !this.checkService.isEmailForced();
  }

  getInvitationEmail(): string {
    return this.invitation?.email ?? null;
  }

  getInvitationKsuiteName(): string {
    return this.invitation?.extras?.ksuite_name;
  }

  generate($event) {
    this.form.get('password').patchValue($event);
    this.form.updateValueAndValidity();
  }

  returnToManager(): void {
    window.location.assign(this.managerUrlToRedirect ?? this.endpoints?.manager);
  }

  copied() {
    this.notificationService.success(translate('Mot de passe copie avec succes'), null, null, {
      icon: 'icon-check-circle-1 success',
    });
  }

  getValidationErrorMessage() {
    return getPasswordValidationMessage(this.passwordValidation);
  }

  // Validators
  private passwordValidator(ctrl: FormControl): ValidationErrors | null {
    return this.passwordValidation.validatePassword(ctrl.value) ? null : { invalid: true };
  }
}
